import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { isAxiosError } from "axios"

import {
  runReport,
  type ReportRunFilterInput,
  type ReportTemplateDetailDto,
  type ReportTemplateFieldDto,
  type ReportTemplateFilterDto,
} from "../../api/reportTemplateService"
import { getPermitDetail, getPermits, type PermitDetailWorker } from "modules/permit/api/permitService"
import { clamp } from "./editorUtils"

const parseDefaultFilterValue = (raw?: string | null): { value: unknown; valueTo?: unknown } | null => {
  if (!raw || !raw.trim()) return null
  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const from =
        (parsed as Record<string, unknown>).from ??
        (parsed as Record<string, unknown>).start ??
        (parsed as Record<string, unknown>).value
      const to = (parsed as Record<string, unknown>).to ?? (parsed as Record<string, unknown>).end
      return { value: from ?? null, valueTo: to ?? null }
    }
    return { value: parsed }
  } catch {
    return { value: raw }
  }
}

const getPreviewPermitIdentity = (row: Record<string, unknown> | null): { permitId: string; permitNumber: string } => {
  if (!row) return { permitId: "", permitNumber: "" }
  const permitIdCandidates = [row.__permitId, row.permit_id, row.permitId]
  const permitNumberCandidates = [row.__permitNumber, row.permit_number, row.permitNumber]
  const permitId =
    permitIdCandidates
      .map((item) => String(item ?? "").trim())
      .find((item) => item.length > 0) ?? ""
  const permitNumber =
    permitNumberCandidates
      .map((item) => String(item ?? "").trim())
      .find((item) => item.length > 0) ?? ""
  return { permitId, permitNumber }
}

type UseReportTemplatePreviewRuntimeParams = {
  template: ReportTemplateDetailDto | null
  fields: ReportTemplateFieldDto[]
  filters: ReportTemplateFilterDto[]
  layoutViewMode: "design" | "preview"
  hasWorkersTable: boolean
  defaultTenantId: string
}

export function useReportTemplatePreviewRuntime({
  template,
  fields,
  filters,
  layoutViewMode,
  hasWorkersTable,
  defaultTenantId,
}: UseReportTemplatePreviewRuntimeParams) {
  const [previewRows, setPreviewRows] = useState<Array<Record<string, unknown>>>([])
  const [previewRowIndex, setPreviewRowIndex] = useState(0)
  const [isLoadingPreviewData, setIsLoadingPreviewData] = useState(false)
  const [previewDataError, setPreviewDataError] = useState<string | null>(null)
  const [previewDataSource, setPreviewDataSource] = useState<"api" | "fallback" | null>(null)
  const [previewElapsedMs, setPreviewElapsedMs] = useState<number | undefined>(undefined)

  const [previewWorkers, setPreviewWorkers] = useState<PermitDetailWorker[]>([])
  const [isLoadingPreviewWorkers, setIsLoadingPreviewWorkers] = useState(false)
  const [previewWorkersError, setPreviewWorkersError] = useState<string | null>(null)
  const previewWorkersRequestIdRef = useRef(0)

  const selectedPreviewRow = useMemo(() => {
    if (previewRows.length === 0) return null
    const safeIndex = clamp(previewRowIndex, 0, Math.max(0, previewRows.length - 1))
    return previewRows[safeIndex] ?? null
  }, [previewRowIndex, previewRows])

  useEffect(() => {
    setPreviewRowIndex((prev) => clamp(prev, 0, Math.max(0, previewRows.length - 1)))
  }, [previewRows.length])

  const resetPreviewRuntime = useCallback(() => {
    setPreviewRows([])
    setPreviewRowIndex(0)
    setPreviewDataError(null)
    setPreviewDataSource(null)
    setPreviewElapsedMs(undefined)
    setPreviewWorkers([])
    setPreviewWorkersError(null)
  }, [])

  const loadLayoutPreviewData = useCallback(async () => {
    if (!template || !template.reportTemplateId) return

    setIsLoadingPreviewData(true)
    setPreviewDataError(null)
    try {
      const columns = (fields ?? [])
        .map((item) => String(item.fieldKey || "").trim())
        .filter(Boolean)

      const filtersPayload: ReportRunFilterInput[] = (filters ?? [])
        .map((filter) => {
          const parsed = parseDefaultFilterValue(filter.defaultValueJson)
          if (!parsed || parsed.value == null || String(parsed.value).trim() === "") return null
          const next: ReportRunFilterInput = {
            fieldKey: filter.fieldKey,
            operator: filter.operator || "=",
            value: parsed.value as string | number | boolean | null | Array<string | number | boolean>,
          }
          if (parsed.valueTo != null && String(parsed.valueTo).trim() !== "") {
            next.valueTo = parsed.valueTo as string | number | boolean | null
          }
          return next
        })
        .filter((item): item is ReportRunFilterInput => item !== null)

      const result = await runReport({
        tenantId: template.tenantId || defaultTenantId,
        templateId: template.reportTemplateId,
        columns,
        filters: filtersPayload.length > 0 ? filtersPayload : undefined,
        pageNumber: 1,
        pageSize: 30,
      })

      const rows = Array.isArray(result.rows) ? result.rows : []
      setPreviewRows(rows)
      setPreviewRowIndex(0)
      setPreviewDataSource(result.source)
      setPreviewElapsedMs(result.elapsedMs)
      if (rows.length === 0) {
        setPreviewDataError("No preview rows found for current template/filter defaults")
      }
    } catch (err) {
      console.error(err)
      setPreviewRows([])
      setPreviewRowIndex(0)
      setPreviewDataSource(null)
      setPreviewElapsedMs(undefined)
      if (isAxiosError(err)) {
        const message = (err.response?.data as { message?: string } | undefined)?.message
        setPreviewDataError(message || "Failed to load preview data from report runtime")
      } else if (err instanceof Error) {
        setPreviewDataError(err.message)
      } else {
        setPreviewDataError("Failed to load preview data from report runtime")
      }
    } finally {
      setIsLoadingPreviewData(false)
    }
  }, [defaultTenantId, fields, filters, template])

  useEffect(() => {
    if (layoutViewMode !== "preview") return
    if (!template?.reportTemplateId) return
    if (isLoadingPreviewData || previewRows.length > 0) return
    loadLayoutPreviewData().catch(() => undefined)
  }, [isLoadingPreviewData, layoutViewMode, loadLayoutPreviewData, previewRows.length, template?.reportTemplateId])

  useEffect(() => {
    const loadWorkers = async () => {
      const requestId = previewWorkersRequestIdRef.current + 1
      previewWorkersRequestIdRef.current = requestId

      if (layoutViewMode !== "preview" || !hasWorkersTable) {
        setPreviewWorkers([])
        setPreviewWorkersError(null)
        setIsLoadingPreviewWorkers(false)
        return
      }

      const { permitId, permitNumber } = getPreviewPermitIdentity(selectedPreviewRow)
      setPreviewWorkers([])
      setPreviewWorkersError(null)
      if (!permitId && !permitNumber) {
        setPreviewWorkersError("No permit id/number in preview row for workers table")
        setIsLoadingPreviewWorkers(false)
        return
      }

      setIsLoadingPreviewWorkers(true)
      try {
        let resolvedPermitId = permitId
        if (!resolvedPermitId && permitNumber) {
          const found = await getPermits({
            tenantId: template?.tenantId || defaultTenantId,
            searchTerm: permitNumber,
            pageNumber: 1,
            pageSize: 1,
          })
          resolvedPermitId = String(found.items?.[0]?.permitId || "").trim()
        }

        if (!resolvedPermitId) {
          if (previewWorkersRequestIdRef.current !== requestId) return
          setPreviewWorkersError("Cannot resolve permit id for workers table")
          return
        }

        const detail = await getPermitDetail(template?.tenantId || defaultTenantId, resolvedPermitId)
        if (previewWorkersRequestIdRef.current !== requestId) return

        const workersRaw = detail?.workers
        const workers = Array.isArray(workersRaw) ? workersRaw : []
        setPreviewWorkers(workers)
        if (workers.length === 0) {
          setPreviewWorkersError("No workers found in this permit")
        }
      } catch (err) {
        console.error(err)
        if (previewWorkersRequestIdRef.current !== requestId) return
        setPreviewWorkers([])
        if (isAxiosError(err)) {
          const message = (err.response?.data as { message?: string } | undefined)?.message
          setPreviewWorkersError(message || "Failed to load workers from permit detail")
        } else if (err instanceof Error) {
          setPreviewWorkersError(err.message)
        } else {
          setPreviewWorkersError("Failed to load workers from permit detail")
        }
      } finally {
        if (previewWorkersRequestIdRef.current !== requestId) return
        setIsLoadingPreviewWorkers(false)
      }
    }

    loadWorkers().catch(() => undefined)
  }, [defaultTenantId, hasWorkersTable, layoutViewMode, selectedPreviewRow, template?.tenantId])

  return {
    previewRows,
    previewRowIndex,
    setPreviewRowIndex,
    isLoadingPreviewData,
    previewDataError,
    previewDataSource,
    previewElapsedMs,
    previewWorkers,
    isLoadingPreviewWorkers,
    previewWorkersError,
    selectedPreviewRow,
    loadLayoutPreviewData,
    resetPreviewRuntime,
  }
}
